import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Globe, Users } from "lucide-react";

const ContactSection = () => {
  return (
    <section
      id="contact"
      className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900 relative overflow-hidden"
    >
      {/* Beautiful layered background */}
      <div className="absolute inset-0">
        {/* Main gradient layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-emerald-900/30"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 via-transparent to-yellow-900/20"></div>

        {/* Large organic shapes */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-br from-emerald-400/8 to-blue-400/12 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-gradient-to-br from-yellow-400/10 to-emerald-400/8 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-blue-400/6 to-yellow-400/8 rounded-full blur-2xl"></div>

        {/* Animated floating orbs */}
        <div className="absolute top-32 left-32 w-24 h-24 bg-gradient-to-br from-emerald-400/15 to-blue-400/10 rounded-full blur-xl animate-pulse delay-300"></div>
        <div className="absolute bottom-32 right-32 w-20 h-20 bg-gradient-to-br from-yellow-400/12 to-emerald-400/8 rounded-full blur-lg animate-bounce delay-1000"></div>
        <div className="absolute top-2/3 left-1/4 w-16 h-16 bg-gradient-to-br from-blue-400/10 to-yellow-400/8 rounded-full blur-lg animate-pulse delay-700"></div>

        {/* Elegant geometric accents */}
        <div className="absolute top-20 right-1/4">
          <div className="w-2 h-2 bg-emerald-400/60 rounded-full animate-pulse"></div>
        </div>
        <div className="absolute bottom-40 left-1/3">
          <div className="w-3 h-3 bg-yellow-400/50 rounded-full animate-bounce delay-500"></div>
        </div>
        <div className="absolute top-1/2 right-20">
          <div className="w-2 h-2 bg-blue-400/70 rounded-full animate-pulse delay-1000"></div>
        </div>

        {/* Sophisticated pattern overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 3px 3px, rgba(255,255,255,0.4) 1px, transparent 0)`,
            backgroundSize: "48px 48px",
          }}
        ></div>

        {/* Top wave decoration */}
        <div className="absolute top-0 left-0 right-0 h-24 opacity-15">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-full rotate-180"
          >
            <path
              d="M0,20 C300,60 600,10 900,40 C1000,60 1100,20 1200,40 L1200,0 L0,0 Z"
              fill="url(#topWaveGradient)"
            />
            <defs>
              <linearGradient
                id="topWaveGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#eab308" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Bottom wave decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <path
              d="M0,60 C300,20 600,80 900,40 C1000,20 1100,60 1200,40 L1200,120 L0,120 Z"
              fill="url(#bottomWaveGradient)"
            />
            <defs>
              <linearGradient
                id="bottomWaveGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#eab308" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-block p-4 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 rounded-full mb-8 border border-white/20 backdrop-blur-sm">
            <Phone className="w-10 h-10 text-yellow-400" />
          </div>
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 drop-shadow-2xl">
            Let's Build the{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-emerald-400 to-blue-400">
              Future Together
            </span>
          </h2>
          <div className="w-32 h-1.5 bg-gradient-to-r from-yellow-400 via-emerald-400 to-blue-400 mx-auto mb-10 rounded-full shadow-lg"></div>
          <p className="text-2xl text-slate-200 max-w-4xl mx-auto font-light drop-shadow-lg">
            Ready to explore partnerships, opportunities, or learn more about
            our subsidiaries? We'd love to connect with you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">
              Send Us a Message
            </h3>
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <input
                  type="text"
                  placeholder="First Name"
                  className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                />
              </div>
              <input
                type="email"
                placeholder="Email Address"
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
              />
              <input
                type="text"
                placeholder="Company & Role"
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
              />
              <textarea
                placeholder="Tell us about your project or inquiry..."
                rows="6"
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent backdrop-blur-sm resize-none transition-all duration-300"
              ></textarea>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-500 via-blue-500 to-yellow-500 hover:from-yellow-500 hover:via-emerald-500 hover:to-blue-500 text-white py-5 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-emerald-500/25"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 shadow-2xl">
              <h3 className="text-3xl font-bold text-white mb-8">
                Get in Touch
              </h3>

              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/30 to-emerald-500/30 rounded-xl flex items-center justify-center flex-shrink-0 border border-yellow-400/30">
                    <Mail className="w-8 h-8 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-2 text-xl">Email</h4>
                    <p className="text-slate-200 text-lg">
                      info@kubameridian.com
                    </p>
                    <p className="text-slate-200 text-lg">
                      partnerships@kubameridian.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/30 to-blue-500/30 rounded-xl flex items-center justify-center flex-shrink-0 border border-emerald-400/30">
                    <Phone className="w-8 h-8 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-2 text-xl">Phone</h4>
                    <p className="text-slate-200 text-lg">
                      +27 (0) 11 XXX XXXX
                    </p>
                    <p className="text-slate-200 text-lg">+243 XXX XXX XXX</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500/30 to-yellow-500/30 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-400/30">
                    <MapPin className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-2 text-xl">
                      Offices
                    </h4>
                    <p className="text-slate-200 text-lg">
                      Johannesburg, South Africa
                    </p>
                    <p className="text-slate-200 text-lg">
                      Democratic Republic of Congo
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links & Social */}
            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 shadow-2xl">
              <h3 className="text-3xl font-bold text-white mb-8">
                Connect With Us
              </h3>

              <div className="flex space-x-4 mb-8">
                {[
                  {
                    icon: <Globe className="w-6 h-6" />,
                    color: "from-yellow-500/20 to-emerald-500/20",
                  },
                  {
                    icon: <Users className="w-6 h-6" />,
                    color: "from-emerald-500/20 to-blue-500/20",
                  },
                  {
                    icon: <Mail className="w-6 h-6" />,
                    color: "from-blue-500/20 to-yellow-500/20",
                  },
                  {
                    icon: <Phone className="w-6 h-6" />,
                    color: "from-yellow-500/20 to-blue-500/20",
                  },
                ].map((social, index) => (
                  <a
                    key={index}
                    href="#"
                    className={`w-14 h-14 bg-gradient-to-br ${social.color} hover:scale-110 rounded-xl flex items-center justify-center transition-all duration-300 group border border-white/20 hover:border-white/40`}
                  >
                    <span className="text-white group-hover:scale-110 transition-transform">
                      {social.icon}
                    </span>
                  </a>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-white font-bold text-xl">Quick Links</h4>
                <div className="space-y-3">
                  {[
                    "Investor Relations",
                    "Career Opportunities",
                    "Corporate Governance",
                    "Sustainability Report",
                  ].map((link, index) => (
                    <a
                      key={index}
                      href="#"
                      className="block text-slate-200 hover:text-yellow-400 transition-colors text-lg font-medium hover:translate-x-2 transform duration-300"
                    >
                      → {link}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
